// components/cards/BasePackageCard.jsx

import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "../../../../language";

const ZONE_TITLE_HEIGHT = {
  xs: 56,
  sm: 52,
  md: 48,
  lg: 90,
  xl: 44,
};

export default function BasePackageCard({
  panel,
  onClick,
  sx = {},
}) {
  const { t } = useTranslation();

  const formattedMarkers = useMemo(() => {
    if (!panel?.biomarkers) return "";

    return panel.rawMarkers
      ? panel.biomarkers
      : panel.biomarkers
          .split(",")
          .map((m) => m.trim())
          .join(" · ");
  }, [panel]);

  return (
    <Box
      onClick={onClick}
      sx={{
        width: "100%",
        transition: "transform 0.22s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          "& .pkg-card-inner": {
            boxShadow: "0 12px 32px rgba(0,0,0,0.09)",
          },
        },
        ...sx,
      }}
    >
      <Box
        className="pkg-card-inner"
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          border: `0.5px solid ${theme.palette.divider}`,
          borderRadius: "16px",
          overflow: "hidden",
          transition: "box-shadow 0.22s ease",
          height: "100%",
        })}
      >
        {/* Image */}
        <Box
          sx={{
            position: "relative",
            height: 180,
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {panel.image ? (
            <Box
              component="img"
              src={panel.image}
              alt={panel.title}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "grey.100",
              }}
            />
          )}

          {panel.price && (
            <Box
              sx={{
                position: "absolute",
                bottom: 12,
                right: 12,
                bgcolor: "rgba(15,15,15,0.52)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: "0.5px solid rgba(255,255,255,0.2)",
                borderRadius: "99px",
                px: 1.5,
                py: 0.7,
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "grey.C50",
                  fontWeight: 400,
                  letterSpacing: "-0.01em",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {panel.price}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Body */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: "14px 16px 16px",
            textAlign: "left",
            flex: 1,
          }}
        >
          {/* Title */}
          <Box sx={{ minHeight: ZONE_TITLE_HEIGHT }}>
            <Typography
              variant="h5"
              sx={{
                textTransform: "uppercase",
                fontWeight: 500,
                letterSpacing: "-0.005em",
                mt: 1,
                mb: 1,
                lineHeight: 1.35,
                color: "greydark.C900",
              }}
            >
              {panel.title}
            </Typography>
          </Box>

          {/* Description */}
          <Typography
            variant="caption"
            sx={{ lineHeight: 1.6, color: "text.disabled", mb: 1 }}
          >
            {panel.subtitle}
          </Typography>

          {/* Divider */}
          <Box sx={{ height: "0.5px", bgcolor: "divider", my: "10px" }} />

          {/* Biomarkers */}
          <Typography
            variant="caption"
            sx={{ lineHeight: 1.6, color: "text.disabled", flex: 1 }}
          >
            {formattedMarkers}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}